/**
 * Promotion-status readout for a LEARNED weight version (PWE 3.6 payoff).
 * Renders the advisory GET /:version/promotion-status result — eligibility,
 * reason, sample sizes, likeness averages — so an admin can see at a glance
 * whether a learned candidate is a safe promotion. Read-only; shape-tolerant
 * because the engine's evaluator owns the exact fields.
 *
 * Ported verbatim from quill-app/quill-admin's PromotionStatusPanel.tsx.
 * Only the type-source import changed.
 */

import React from 'react';
import { Box, Chip, Typography, CircularProgress, Stack } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import { PromotionStatus } from '../../../services/quillAdminService';

const PRETTY: Record<string, string> = {
  eligible: 'Eligible',
  reason: 'Reason',
  sample_size: 'Sample size',
  candidate_sample_size: 'Candidate samples',
  baseline_sample_size: 'Baseline samples',
  likeness_delta: 'Likeness Δ',
  candidate_likeness_avg: 'Candidate likeness avg',
  baseline_likeness_avg: 'Baseline likeness avg',
  harness_regressed: 'Harness regressed',
};

function renderValue(v: unknown): string {
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(3);
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (v == null) return '—';
  return String(v);
}

const PromotionStatusPanel: React.FC<{ status?: PromotionStatus; loading?: boolean }> = ({
  status,
  loading,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption">Evaluating promotion gate…</Typography>
      </Box>
    );
  }
  if (!status) return null;

  const eligible = status.eligible === true;
  const entries = Object.entries(status).filter(([k]) => k !== 'eligible');

  return (
    <Box
      sx={{
        p: 1.5,
        mt: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        bgcolor: 'action.hover',
      }}
      data-testid="promotion-status"
    >
      <Chip
        icon={eligible ? <CheckCircleIcon /> : <BlockIcon />}
        label={eligible ? 'Safe to promote' : 'Not eligible'}
        color={eligible ? 'success' : 'default'}
        size="small"
        sx={{ mb: 1 }}
      />
      <Stack spacing={0.3}>
        {entries.map(([k, v]) => (
          <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {PRETTY[k] || k}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {renderValue(v)}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default PromotionStatusPanel;
